import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { SPP_REPORTS_PROD_STAT_NETGEN_BY_PROGRAM_TYPE } from '@graphql/sppReports.gql';
import { LOG_EXPORT_AUDIT_EVENT } from 'graphql/audit.gql';
import { exportToExcelMultiSheet, ExportColumn, ExportSheet } from '@utils/excelExport';
import { useSelectedPeriod } from '@context/SelectedPeriodContext';

export type SPPMeterData = {
  meter_id: number;
  meter_number: string;
  location: string;
  company: string;
  generation_type: string;
  jan?: number | null;
  feb?: number | null;
  mar?: number | null;
  apr?: number | null;
  may?: number | null;
  jun?: number | null;
  jul?: number | null;
  aug?: number | null;
  sep?: number | null;
  oct?: number | null;
  nov?: number | null;
  dec?: number | null;
  ytd_total: number;
};

export type SPPReportsData = {
  ippMeters: SPPMeterData[];
  comfitMeters: SPPMeterData[];
  maxcapMeters: SPPMeterData[];
  communitySolarMeters: SPPMeterData[];
};

export const useSppReportsProdStatNetGenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedYear: ctxYear, setYear } = useSelectedPeriod();
  const [logExportAudit] = useMutation(LOG_EXPORT_AUDIT_EVENT);

  // URL param takes precedence, then context year (if set), then current year
  const yearParam = searchParams.get('year');
  const currentYear = yearParam ? Number.parseInt(yearParam, 10) : (ctxYear ?? new Date().getFullYear());

  // Disable next button if we're at current year
  const currentDate = new Date();
  const isNextYearDisabled = currentYear >= currentDate.getFullYear();

  // Navigate to a different year
  const navigateYear = (direction: number) => {
    const newYear = currentYear + direction;
    const params = new URLSearchParams(searchParams);
    params.set('year', newYear.toString());
    setYear(newYear);

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: false }
    );
  };

  // Fetch data using GraphQL query
  const { data: queryData, loading, error } = useQuery(SPP_REPORTS_PROD_STAT_NETGEN_BY_PROGRAM_TYPE, {
    variables: {
      year: currentYear,
    },
    fetchPolicy: 'cache-and-network',
  });

  const data: SPPReportsData = {
    ippMeters: queryData?.sppReportsProdStatNetGenByProgramType?.ipp_meters || [],
    comfitMeters: queryData?.sppReportsProdStatNetGenByProgramType?.comfit_meters || [],
    maxcapMeters: queryData?.sppReportsProdStatNetGenByProgramType?.maxcap_meters || [],
    communitySolarMeters: queryData?.sppReportsProdStatNetGenByProgramType?.community_solar_meters || [],
  };

  const handleExportToExcel = () => {
    const exportColumns: ExportColumn[] = [
      { header: 'Company', dataIndex: 'company', format: 'text' },
      { header: 'Name', dataIndex: 'location', format: 'text' },
      { header: 'Meter Number', dataIndex: 'meter_number', format: 'text' },
      { header: 'Generation Type', dataIndex: 'generation_type', format: 'text' },
      { header: 'Jan', dataIndex: 'jan', format: 'number' },
      { header: 'Feb', dataIndex: 'feb', format: 'number' },
      { header: 'Mar', dataIndex: 'mar', format: 'number' },
      { header: 'Apr', dataIndex: 'apr', format: 'number' },
      { header: 'May', dataIndex: 'may', format: 'number' },
      { header: 'Jun', dataIndex: 'jun', format: 'number' },
      { header: 'Jul', dataIndex: 'jul', format: 'number' },
      { header: 'Aug', dataIndex: 'aug', format: 'number' },
      { header: 'Sep', dataIndex: 'sep', format: 'number' },
      { header: 'Oct', dataIndex: 'oct', format: 'number' },
      { header: 'Nov', dataIndex: 'nov', format: 'number' },
      { header: 'Dec', dataIndex: 'dec', format: 'number' },
      { header: 'YTD Total', dataIndex: 'ytd_total', format: 'number' },
    ];

    const sheets: ExportSheet<SPPMeterData>[] = [
      { name: 'IPP', data: data.ippMeters, columns: exportColumns },
      { name: 'COMFIT', data: data.comfitMeters, columns: exportColumns },
      { name: 'AGC Wind Farm', data: data.maxcapMeters, columns: exportColumns },
      { name: 'Community Solar', data: data.communitySolarMeters, columns: exportColumns },
    ];

    exportToExcelMultiSheet(sheets, `SPP_Prod_Stat_NetGen_${currentYear}.xlsx`);

    logExportAudit({
      variables: { dashboard: 'SppReportsProdStatNetGen', monthYear: String(currentYear) },
    }).catch((err) => console.error('Failed to log export audit event:', err));
  };

  return {
    currentYear,
    navigateYear,
    isNextYearDisabled,
    data,
    loading,
    error,
    handleExportToExcel,
  };
};
